import { useMemo } from 'react';
import { isNil } from 'lodash';
// Import Types
import type { TableData } from 'types';
// Import UI Components
import { Icon } from '@geneui/components';
// Import Components
import CopyToClipboard from 'components/common/CopyToClipboard';
// Import Services
import { Helpers } from 'services';
// Import Constants
import {
    DEFAULT_SORTING,
    DynamicBonusTypes,
    TemplateTypesLabels,
    FilterComparisons,
    BonusTypesLabels,
    BonusTypesIcons,
    DataTableColumnsCustomTypes,
    l,
    ExpirationTypes,
    Position,
} from 'constants/common';
// Import Hooks
import { usePartnerDateFormats } from 'hooks/UsePartnerDateFormats';

const { Equals, Like } = FilterComparisons;
const { generateRequestFilterField, customMomentWithoutTimezoneConversion } = Helpers;

export const useDataTableColumnsConfigs = (t) => {
    const { partnerDateTimeFormat } = usePartnerDateFormats();

    return useMemo(
        () => ({
            columns: [
                {
                    text: t(l.ID),
                    dataKey: 'Id',
                    sortable: true,
                    colRenderer: (id) => <CopyToClipboard>{id}</CopyToClipboard>,
                },
                {
                    text: t(l.Title),
                    dataKey: 'Name',
                    sortable: true,
                    colRenderer: (title) => <CopyToClipboard position={Position.Left}>{title}</CopyToClipboard>,
                },
                {
                    text: t(l.Type),
                    dataKey: 'Type',
                    sortable: true,
                    colRenderer: (type) => (
                        <div className="crm-object-type-label">
                            <Icon type={BonusTypesIcons[type] ?? 'bc-icon-promotional-bonus-48'} />
                            <span className="crm-object-type-text">{BonusTypesLabels[type]}</span>
                        </div>
                    ),
                },

                {
                    type: DataTableColumnsCustomTypes.Date,
                    text: t(l.StartDate),
                    dataKey: 'StartDate',
                    sortable: true,
                    formatter: (StartDate) => {
                        return customMomentWithoutTimezoneConversion(StartDate).format(partnerDateTimeFormat);
                    },
                },
                {
                    type: DataTableColumnsCustomTypes.Date,
                    text: t(l.EndDate),
                    dataKey: 'EndDate',
                    sortable: true,
                    formatter: (EndDate) => {
                        return isNil(EndDate)
                            ? ''
                            : customMomentWithoutTimezoneConversion(EndDate).format(partnerDateTimeFormat);
                    },
                },
            ],
        }),
        [partnerDateTimeFormat, t],
    );
};

export const DynamicBonusTypesRadioGroupOptionsConfig = [
    { label: l.Object, tooltip: l.TooltipBonusCalculationObject, value: DynamicBonusTypes.Object },
    { label: l.KPI, tooltip: l.TooltipBonusCalculationKPI, value: DynamicBonusTypes.KPI },
    { label: l.FromFile, tooltip: l.TooltipBonusCalculationFromFile, value: DynamicBonusTypes.FromFile },
];

export const INTERNAL_TEMPLATE_LIST_CONFIG: TableData = {
    tableData: {
        paging: {
            PageSize: 1000,
            PageNumber: 1,
        },
        sorting: DEFAULT_SORTING,
        filters: [
            generateRequestFilterField('Name', Like, ''),
            generateRequestFilterField('DeliveryMethod', Equals, [TemplateTypesLabels.Internal]),
            generateRequestFilterField('State', Equals, [TemplateTypesLabels.Internal]),
        ],
    },
};

export const ExpirationTypesDropdownData = (t) => {
    return [
        { label: t(l.FromPayoutDate), value: ExpirationTypes.ExpirationPeriod },
        { label: t(l.ToGivenDate), value: ExpirationTypes.ExpirationDate },
    ];
};
